import { LitElement, html, svg, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { apiParams } from '../lib/filters.js';
import { withFormats } from '../lib/table.js';
import {
  DECISION_GROUPS,
  DECISION_STATES,
  REMEDIES,
  REMEDY_LABELS,
  calendarLayout,
  decisionColumns,
  decisionModel,
  decisionRows,
  decisionSummary,
} from '../lib/decisions.js';
import { hours as formatHours, percent, longDate, shortDate, count } from '../lib/format.js';

/** The shared filters this view reads. */
const PARAM_KEYS = Object.freeze(['run_id', 'dimension', 'from', 'weeks']);

/** The plot's own coordinate space. */
const PLOT = Object.freeze({
  width: 760,
  gutter: 190,
  laneHeight: 32,
  top: 30,
  marker: 6,
  label: 26,
});

const GROUP_LABELS = { date: 'By date', remedy: 'By remedy', signal: 'By overload' };
const STATE_LABELS = { past: 'Already past', due: 'Due now', ahead: 'Still ahead' };

const plural = (n, one, many = `${one}s`) => `${count(n)} ${n === 1 ? one : many}`;
const exact = (v) => formatHours(v, { exact: true });

/**
 * The decision calendar (Design Review V-17; TRD 8.6; Phase 49): by when must we act to prevent
 * each forecast overload.
 *
 * The Gantt above this card says when the work is. This one says when the **decisions** about it
 * are due, which is a different calendar and usually an earlier one. "November is 118 per cent"
 * is information; "book a freelancer by 6 October, and the requisition needed opening in August"
 * is an action, and the difference between the two is entirely the lead times.
 *
 * Every date is the overload window's first week less that remedy's lead time, recomputed from
 * the settings as they stand rather than read back from the run — a lead time is a policy, not a
 * forecast, so correcting one has to move the calendar now rather than at the next run. The card
 * says which dates moved and what the run itself had said.
 *
 * Every figure comes from `GET /forecast/decisions` and every position from `lib/decisions.js`.
 */
class DecisionCalendar extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _group: { state: true },
    _remedies: { state: true },
    _states: { state: true },
    _q: { state: true },
    _hover: { state: true },
    _pinned: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._group = 'date';
    this._remedies = [...REMEDIES];
    this._states = [...DECISION_STATES];
    this._q = '';
    this._hover = null;
    this._pinned = null;
    this._tableOpen = false;
    this._requestKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
        min-width: 0;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
        min-width: 0;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .n[data-state='past'] {
        color: var(--status-overloaded);
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 8px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        font: inherit;
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--surface);
        background: var(--ink-strong);
      }
      .field {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .field input {
        font: inherit;
        font-size: 12.5px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        padding: 3px 9px;
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--brand);
        cursor: pointer;
        padding: 0;
        text-decoration: underline;
      }
      svg.plot {
        display: block;
        width: 100%;
        height: auto;
        overflow: visible;
      }
      .marker {
        cursor: pointer;
        stroke: var(--surface);
        stroke-width: 1.25;
      }
      .marker[data-state='past'] {
        fill: var(--status-overloaded);
      }
      .marker[data-state='due'] {
        fill: var(--status-caution);
      }
      .marker[data-state='ahead'] {
        fill: var(--status-healthy);
      }
      .row[data-dim='true'] .marker,
      .row[data-dim='true'] text {
        opacity: 0.2;
      }
      .today {
        stroke: var(--ink-strong);
        stroke-width: 1.5;
      }
      .lane-line {
        stroke: var(--border-soft);
        stroke-width: 1;
      }
      text {
        font-size: 11.5px;
        fill: var(--ink);
      }
      text.axis {
        font-size: 10.5px;
        fill: var(--ink-muted);
      }
      text.today-label {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        fill: var(--ink-strong);
      }
      .legends {
        margin: 10px 0 2px;
      }
      ul.legend {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 14px;
        list-style: none;
        margin: 0;
        padding: 0;
        font-size: 11.5px;
        color: var(--ink-muted);
      }
      ul.legend li {
        display: inline-flex;
        align-items: center;
        gap: 5px;
      }
      .sw {
        display: inline-block;
        width: 11px;
        height: 11px;
        border-radius: 50%;
        border: 1px solid var(--border-soft);
      }
      p.note {
        margin: 8px 0 0;
        font-size: 12px;
        line-height: 1.5;
        color: var(--ink-muted);
        max-width: 92ch;
      }
      .state {
        padding: 22px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      data-table {
        display: block;
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Data

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
    // An admin changing a lead time on the settings page changes every date here, so the card
    // listens for it rather than waiting for a reload.
    this._onLeadTimes = () => {
      this._requestKey = null;
      void this._maybeFetch();
    };
    window.addEventListener('lead-times-changed', this._onLeadTimes);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
    window.removeEventListener('lead-times-changed', this._onLeadTimes);
  }

  _params() {
    const { run } = this.appState;
    if (!run) return null;
    return apiParams(this.appState.filters, PARAM_KEYS);
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/decisions', params);
      if (this._requestKey !== key) return;
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Derived

  get _model() {
    if (!this._data) return null;
    const key = `${this._group}|${this._remedies.join(',')}|${this._states.join(',')}|${this._q}`;
    if (this._modelFor !== this._data || this._modelKey !== key) {
      this._model_ = decisionModel(this._data, {
        group: this._group,
        remedies: this._remedies,
        states: this._states,
        q: this._q,
      });
      this._modelFor = this._data;
      this._modelKey = key;
    }
    return this._model_;
  }

  get _focus() {
    return this._hover ?? this._pinned;
  }

  _pin(key) {
    this._pinned = this._pinned === key ? null : key;
  }

  _toggle(list, value, all) {
    const has = list.includes(value);
    const next = has ? list.filter((v) => v !== value) : [...list, value];
    return next.length ? next : [...all];
  }

  _show(event, title, rows, footer) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title,
      rows: rows.filter(Boolean),
      footer: footer || '',
    });
  }

  _hide() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  _showDecision(event, d) {
    this._show(
      event,
      `${d.label} — ${d.entity_label}`,
      [
        { label: 'Decide by', value: longDate(d.decide_by) },
        {
          label: d.state === 'past' ? 'Passed' : 'In',
          value: `${plural(Math.abs(d.days_from_today), 'day')}${d.state === 'past' ? ' ago' : ''}`,
        },
        { label: 'Lead time', value: `${plural(d.lead_time_weeks, 'week')}` },
        { label: 'Overload starts', value: longDate(d.window_start) },
        { label: 'For', value: plural(d.duration_weeks, 'week') },
        { label: 'Hours over', value: exact(d.hours_over) },
        { label: 'Peak allocation', value: percent(d.utilization) },
        d.moved ? { label: 'The run said', value: longDate(d.stored_decide_by) } : null,
      ],
      d.lead_time_source === 'by_discipline'
        ? `${d.canonical_service} has its own lead time for this remedy`
        : d.note,
    );
  }

  // -------------------------------------------------------------------------------------------
  // The chart

  _plot(model) {
    const inner = PLOT.width - PLOT.gutter - 30;
    const layout = calendarLayout(model, {
      width: inner,
      laneHeight: PLOT.laneHeight,
      top: PLOT.top,
    });
    const focus = this._focus;
    const height = layout.height + 20;

    return svg`
      <svg
        class="plot"
        viewBox="0 0 ${PLOT.width} ${height}"
        role="img"
        aria-label=${`${plural(layout.markers.length, 'decision')} against today`}
        @pointerleave=${() => {
          this._hover = null;
          this._hide();
        }}
      >
        <g transform=${`translate(${PLOT.gutter} 0)`}>
          ${layout.lanes.map(
            (lane) => svg`<g>
              <line
                class="lane-line"
                x1="0"
                x2=${layout.width}
                y1=${lane.centre}
                y2=${lane.centre}
              ></line>
              <text
                class="label"
                x="-10"
                y=${lane.centre}
                text-anchor="end"
                dominant-baseline="middle"
              >${
                String(lane.label).length > PLOT.label
                  ? `${String(lane.label).slice(0, PLOT.label - 1)}…`
                  : lane.label
              }</text>
            </g>`,
          )}
          <line
            class="today"
            x1=${layout.today_x}
            x2=${layout.today_x}
            y1=${PLOT.top - 16}
            y2=${layout.height}
          ></line>
          <text class="today-label" x=${layout.today_x} y=${PLOT.top - 20} text-anchor="middle">
            Today
          </text>
          <text class="axis" x="0" y=${layout.height + 14}>
            ${shortDate(layout.domain.from)}
          </text>
          <text class="axis" x=${layout.width} y=${layout.height + 14} text-anchor="end">
            ${shortDate(layout.domain.to)}
          </text>
          ${repeat(
            layout.markers,
            (m) => m.key,
            (m) => svg`<g
              class="row"
              data-key=${m.key}
              data-state=${m.state}
              data-dim=${Boolean(focus) && focus !== m.key}
            >
              <circle
                class="marker mark"
                role="button"
                tabindex="0"
                data-state=${m.state}
                data-remedy=${m.decision.remedy}
                data-decide-by=${m.decision.decide_by}
                cx=${m.x}
                cy=${m.y}
                r=${PLOT.marker}
                @pointerenter=${(e) => {
                  this._hover = m.key;
                  this._showDecision(e, m.decision);
                }}
                @pointermove=${(e) => this._showDecision(e, m.decision)}
                @focus=${() => (this._hover = m.key)}
                @blur=${() => (this._hover = null)}
                @click=${() => this._pin(m.key)}
                @keydown=${(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this._pin(m.key);
                  }
                }}
              >
                <title>
                  ${m.decision.label} for ${m.decision.entity_label} by
                  ${longDate(m.decision.decide_by)}
                </title>
              </circle>
            </g>`,
          )}
        </g>
      </svg>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _controls() {
    const group = (options, current, onPick, label) => {
      const slug = label.toLowerCase();
      return html`
        <span class="segmented">
          <span>${label}</span>
          <span class="group" role="group" aria-label=${label}>
            ${options.map(
              ([key, text]) => html`
                <button
                  type="button"
                  data-control=${slug}
                  data-key=${key}
                  aria-pressed=${
                    (Array.isArray(current) ? current.includes(key) : current === key)
                      ? 'true'
                      : 'false'
                  }
                  @click=${() => onPick(key)}
                >
                  ${text}
                </button>
              `,
            )}
          </span>
        </span>
      `;
    };
    return html`
      <div class="controls">
        ${group(
          DECISION_GROUPS.map((g) => [g, GROUP_LABELS[g]]),
          this._group,
          (k) => (this._group = k),
          'Lanes',
        )}
        ${group(
          DECISION_STATES.map((s) => [s, STATE_LABELS[s]]),
          this._states,
          (k) => (this._states = this._toggle(this._states, k, DECISION_STATES)),
          'Show',
        )}
        ${group(
          REMEDIES.map((r) => [r, REMEDY_LABELS[r]]),
          this._remedies,
          (k) => (this._remedies = this._toggle(this._remedies, k, REMEDIES)),
          'Remedy',
        )}
        <label class="field">
          Find
          <input
            type="search"
            placeholder="role or person"
            .value=${this._q}
            @input=${(e) => (this._q = e.target.value)}
          />
        </label>
        ${
          this._pinned
            ? html`<button class="link" type="button" @click=${() => (this._pinned = null)}>
                Clear the isolation
              </button>`
            : nothing
        }
      </div>
    `;
  }

  _kpis(model, summary) {
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${summary.next ? shortDate(summary.next.decide_by) : '—'}</div>
          <div class="l">${summary.next ? summary.next.label.toLowerCase() : 'nothing due'}</div>
        </div>
        <div class="kpi">
          <div class="n" data-state="past">${count(summary.past)}</div>
          <div class="l">already past</div>
        </div>
        <div class="kpi">
          <div class="n">${count(summary.due)}</div>
          <div class="l">due inside a fortnight</div>
        </div>
        <div class="kpi">
          <div class="n">${count(summary.signals)}</div>
          <div class="l">overloads to prevent</div>
        </div>
      </div>
    `;
  }

  _legend(model) {
    return html`
      <div class="legends">
        <ul class="legend">
          <li><span class="sw" style="background: var(--status-overloaded)"></span>already past</li>
          <li>
            <span class="sw" style="background: var(--status-caution)"></span>due inside a fortnight
          </li>
          <li><span class="sw" style="background: var(--status-healthy)"></span>still ahead</li>
          <li>
            Lead times:
            ${REMEDIES.map((r) => `${REMEDY_LABELS[r]} ${model.lead_times?.[r] ?? 0}w`).join(' · ')}
          </li>
        </ul>
      </div>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    return `today is ${longDate(model.today)} · ${longDate(model.from)} to ${longDate(
      model.to,
    )} · ${plural(model.weeks_count, 'week')}`;
  }

  _notes(model, summary) {
    const notes = [];
    notes.push(
      html`<p class="note">
        Each marker is a decision, at the date it must be taken for its remedy to arrive before the
        overload starts: the overload's first week less that remedy's lead time. Overload in
        November is information; a freelancer to book by
        ${summary.next ? longDate(summary.next.decide_by) : 'a date'} is an action, and the
        difference between them is entirely the lead times.
      </p>`,
    );

    if (summary.past > 0)
      notes.push(
        html`<p class="note" data-note="past">
          ${plural(summary.past, 'deadline')} have already passed. They are drawn to the left of
          today rather than dropped, because the remedy is not gone — it is late, and a conversation
          about a late hire is a different conversation from one about no hire.
        </p>`,
      );

    if (summary.staffing_too_late.length)
      notes.push(
        html`<p class="note" data-note="too-late">
          ${plural(summary.staffing_too_late.length, 'overload')} can no longer be staffed in time:
          every lead time that brings people is longer than the weeks remaining. Moving or rescoping
          the work is what is left — that has no lead time, which is why it is the one marker that
          is never late — and the earlier this page is read, the fewer of these there are.
        </p>`,
      );

    if (model.settings_moved)
      notes.push(
        html`<p class="note" data-note="moved">
          ${plural(summary.moved, 'date')} differ from what the run itself computed, because a lead
          time has been changed in settings since. These dates are the current policy; the run's own
          figure is in the table. Nothing was re-forecast — only the arithmetic on the front.
        </p>`,
      );

    if (summary.hidden > 0)
      notes.push(
        html`<p class="note" data-note="hidden">
          The controls are hiding ${plural(summary.hidden, 'decision')}. The table carries them all.
        </p>`,
      );

    if (model.withheld_count ?? 0 > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${plural(model.withheld_count, 'overload')} on people outside what you may see are not
          drawn. Their service and department signals are, because those are nobody's own hours.
        </p>`,
      );

    return html`${notes}`;
  }

  get _formats() {
    return {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : exact(v)),
        percent: (v) => percent(v),
        count: (v) => (v === null || v === undefined ? '—' : count(v)),
      },
    };
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const model = this._model;
    const summary = model ? decisionSummary(model) : null;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to decide.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!this._data && this._loading)
      body = html`<div class="state">Working out what is due…</div>`;
    else if (!model) body = html`<div class="state">Nothing to draw.</div>`;
    else if (!model.decisions.length)
      body = html`
        ${this._controls()}
        <div class="state" data-note="empty">
          ${
            model.all.length
              ? 'Every decision is hidden by the controls.'
              : 'No overload in this window, so there is nothing to decide by a date.'
          }
        </div>
      `;
    else
      body = html`
        ${this._controls()} ${this._plot(model)} ${this._legend(model)}
        ${this._notes(model, summary)}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Every decision — ${this._subtitle(model)}`}
            export-name="decision-calendar"
            page-size="25"
            dense
            .columns=${withFormats(decisionColumns(), this._formats)}
            .rows=${decisionRows(model)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Decision calendar</div>
            <h2>By when must we act?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${summary ? this._kpis(model, summary) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('decision-calendar', DecisionCalendar);
